import type { FC } from 'react';

import { Auth0Provider } from '@auth0/auth0-react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';

import { ImpersonationBar } from 'layouts/2Pane/ImpersonationBar';
import { AuthProvider } from 'shared/contexts/Auth0Context';
import { FiltersContextProvider } from 'shared/contexts/FiltersContext';
import { SandboxContextProvider } from 'shared/contexts/SandboxContext';
import { TenancyContextProvider } from 'shared/contexts/TenancyContext';
import { theme } from 'shared/theme';
import { ReactErrorView } from 'views/errors/ReactErrorView';

import { HubspotChat } from './integrations/HubspotChat';
import { KnockProvider } from './integrations/KnockProvider';
import { PostHogProvider } from './integrations/PostHog';
import { ScrollReset } from './integrations/ScrollReset';
import './integrations/sentry';

import { ModalProvider } from 'react-modal-hook';
import { TransitionGroup } from 'react-transition-group';

import { IncidentDialog } from 'shared/lib/statusPage/IncidentDialog';

import { SnackbarProvider } from './integrations/SnackbarProvider';
import { WithQueryClient } from './integrations/WithQueryClient';
import { RoutesWrapper } from './routes/RoutesWrapper';
import { GlobalStyles } from './styles/GlobalStyles';

const history = createBrowserHistory();

LicenseInfo.setLicenseKey(
  '5a3bb9a5bb16182e2bb2a6bb76ceadfdTz0xMjM5MzQsRT0xODAwNjYyMzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=',
);

const App: FC = () => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <StylesThemeProvider theme={theme}>
          <Router history={history}>
            <Sentry.ErrorBoundary fallback={({ resetError }) => <ReactErrorView onReset={resetError} />}>
              <SnackbarProvider>
                <WithQueryClient>
                  {!import.meta.env.PROD && <ReactQueryDevtools buttonPosition="bottom-right" initialIsOpen={false} />}
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Auth0Provider
                      authorizationParams={{
                        redirect_uri: window.location.origin,
                      }}
                      clientId={import.meta.env.VITE_APP_AUTH0_CLIENT_ID ?? ''}
                      domain={import.meta.env.VITE_APP_AUTH0_DOMAIN ?? ''}
                      onRedirectCallback={(appState) => {
                        history.replace(appState?.backUrl || window.location.pathname);
                      }}
                    >
                      <AuthProvider>
                        <PostHogProvider>
                          <KnockProvider>
                            <ImpersonationBar />
                            <SandboxContextProvider>
                              <TenancyContextProvider>
                                <ModalProvider rootComponent={TransitionGroup}>
                                  <FiltersContextProvider>
                                    <GlobalStyles />
                                    <ScrollReset />
                                    <HubspotChat />
                                    <RoutesWrapper />
                                    <IncidentDialog />
                                  </FiltersContextProvider>
                                </ModalProvider>
                              </TenancyContextProvider>
                            </SandboxContextProvider>
                          </KnockProvider>
                        </PostHogProvider>
                      </AuthProvider>
                    </Auth0Provider>
                  </LocalizationProvider>
                </WithQueryClient>
              </SnackbarProvider>
            </Sentry.ErrorBoundary>
          </Router>
        </StylesThemeProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
